import React from "react";

import ChevronDown from "@uui/ChevronDown";
import { find, isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import FilterCountChip from "@app/analytics/components/FilterCountChip";
import Tag from "@app/analytics/components/Tag";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import type { TagCategory } from "@app/analytics/MeetingReports/types";
import { Tag as ITag } from "@app/common/types";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Checkbox from "@rui/Checkbox";
import { Row } from "@rui/Stack";

import { Menu, MenuButton, MenuItem } from "./styles";

const TagSection = ({
  category,
  isCategorySelected,
  selectedTags,
  tags,
  onToggle,
}: {
  category: {
    value: TagCategory;
    label: string;
  };
  isCategorySelected: boolean;
  selectedTags: ITag[];
  tags: ITag[];
  onToggle: (id: string) => void;
}) => {
  if (isEmpty(tags)) return null;

  const handleToggle = (id: string) => () => {
    onToggle(id);
  };

  return (
    <>
      <MenuItem
        isCategory
        value={category.value}
        onClick={handleToggle(category.value)}
      >
        <Row variant="apart" sx={{ width: "100%" }}>
          <Typography variant="bodySecondaryMed">
            {category.label} ({tags.length})
          </Typography>
          <Checkbox variant="filled" checked={isCategorySelected} />
        </Row>
      </MenuItem>
      {tags.map((tag) => (
        <MenuItem key={tag.id} value={tag.id} onClick={handleToggle(tag.id)}>
          <Row variant="apart" sx={{ width: "100%" }}>
            <Tag tag={tag} />
            <Checkbox
              variant="filled"
              checked={Boolean(find({ id: tag.id }, selectedTags))}
            />
          </Row>
        </MenuItem>
      ))}
    </>
  );
};

const TagFilterMenu = () => {
  const { anchorEl, open, onClose, onClick } = useAnchorElement();
  const { byCategory, selectedCategories, selectedTags, toggleTag } =
    useReportsTable((state) => ({
      ...state.tags,
      toggleTag: state.toggleTag,
    }));

  return (
    <>
      <MenuButton
        aria-controls={open ? "tag-filter-menu" : undefined}
        aria-haspopup
        aria-expanded={open ? "true" : undefined}
        onClick={onClick}
        endIcon={<ChevronDown sx={{ fontSize: "20px" }} />}
      >
        <Typography variant="bodySecondaryMed">Tag</Typography>
        {selectedTags.length > 0 && (
          <FilterCountChip>{selectedTags.length}</FilterCountChip>
        )}
      </MenuButton>
      <Menu
        id="tag-filter-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <TagSection
          category={{
            value: "auto",
            label: "Auto Tags",
          }}
          isCategorySelected={selectedCategories.includes("auto")}
          selectedTags={selectedTags}
          tags={byCategory.auto}
          onToggle={toggleTag}
        />
        <TagSection
          category={{
            value: "custom",
            label: "Custom Tags",
          }}
          isCategorySelected={selectedCategories.includes("custom")}
          selectedTags={selectedTags}
          tags={byCategory.custom}
          onToggle={toggleTag}
        />
      </Menu>
    </>
  );
};

export default TagFilterMenu;
